import React, { useState, useEffect } from 'react';
import { post, put } from '../../api';
import './CustomersForm.css';

const CustomersForm = ({ customer, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    primaryContact: {
      name: '',
      phone: '',
      email: ''
    },
    roles: {
      buyer: {
        name: '',
        phone: '',
        email: ''
      },
      owner: {
        name: '',
        phone: '',
        email: ''
      },
      user: {
        name: '',
        phone: '',
        email: ''
      }
    },
    otherContacts: []
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const isEditing = !!customer;

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || '',
        primaryContact: {
          name: customer.primaryContact?.name || '',
          phone: customer.primaryContact?.phone?.raw || '',
          email: customer.primaryContact?.email || ''
        },
        roles: {
          buyer: {
            name: customer.roles?.buyer?.name || '',
            phone: customer.roles?.buyer?.phone?.raw || '',
            email: customer.roles?.buyer?.email || ''
          },
          owner: {
            name: customer.roles?.owner?.name || '',
            phone: customer.roles?.owner?.phone?.raw || '',
            email: customer.roles?.owner?.email || ''
          },
          user: {
            name: customer.roles?.user?.name || '',
            phone: customer.roles?.user?.phone?.raw || '',
            email: customer.roles?.user?.email || ''
          }
        },
        otherContacts: customer.otherContacts || []
      });
    }
  }, [customer]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Customer name is required';
    }

    if (formData.primaryContact.email && !isValidEmail(formData.primaryContact.email)) {
      newErrors['primaryContact.email'] = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      if (isEditing) {
        await put(`/contacts/customers/${customer.customerId}`, formData);
      } else {
        await post('/contacts/customers', formData);
      }

      const message = isEditing ? 'Customer updated successfully' : 'Customer created successfully';
      onSuccess(message);

    } catch (error) {
      console.error('Error saving customer:', error);
      setErrors({ submit: error.response?.data?.error || error.message || 'Failed to save customer' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleRoleChange = (role, field, value) => {
    setFormData(prev => ({
      ...prev,
      roles: {
        ...prev.roles,
        [role]: {
          ...prev.roles[role],
          [field]: value
        }
      }
    }));
  };

  const addOtherContact = () => {
    setFormData(prev => ({
      ...prev,
      otherContacts: [...prev.otherContacts, { name: '', phone: '', email: '' }]
    }));
  };

  const removeOtherContact = (index) => {
    setFormData(prev => ({
      ...prev,
      otherContacts: prev.otherContacts.filter((_, i) => i !== index)
    }));
  };

  const updateOtherContact = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      otherContacts: prev.otherContacts.map((contact, i) => 
        i === index ? { ...contact, [field]: value } : contact
      )
    }));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? 'Edit Customer' : 'Add New Customer'}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="customer-form">
          {errors.submit && (
            <div className="error-message">{errors.submit}</div>
          )}

          <div className="form-section">
            <h3>Basic Information</h3>
            
            <div className="form-group">
              <label htmlFor="name">
                Customer Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={errors.name ? 'error' : ''}
                placeholder="Enter customer name"
              />
              {errors.name && <span className="field-error">{errors.name}</span>}
            </div>
          </div>

          <div className="form-section">
            <h3>Primary Contact</h3>
            
            <div className="form-group">
              <label htmlFor="contactName">Contact Name</label>
              <input
                type="text"
                id="contactName"
                value={formData.primaryContact.name}
                onChange={(e) => handleInputChange('primaryContact.name', e.target.value)}
                placeholder="Enter contact name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="contactPhone">Phone Number</label>
              <input
                type="tel"
                id="contactPhone"
                value={formData.primaryContact.phone}
                onChange={(e) => handleInputChange('primaryContact.phone', e.target.value)}
                placeholder="e.g., 0724 931504 or +254 724 931504"
              />
              <small className="field-help">Enter phone number in any format</small>
            </div>

            <div className="form-group">
              <label htmlFor="contactEmail">Email Address</label>
              <input
                type="email"
                id="contactEmail"
                value={formData.primaryContact.email}
                onChange={(e) => handleInputChange('primaryContact.email', e.target.value)}
                className={errors['primaryContact.email'] ? 'error' : ''}
                placeholder="Enter email address"
              />
              {errors['primaryContact.email'] && (
                <span className="field-error">{errors['primaryContact.email']}</span>
              )}
            </div>
          </div>

          <div className="form-section">
            <h3>Role Contacts</h3>
            
            <div className="role-contacts">
              <div className="role-group">
                <h4>Buyer</h4>
                <div className="form-group">
                  <label htmlFor="buyerName">Buyer Name</label>
                  <input
                    type="text"
                    id="buyerName"
                    value={formData.roles.buyer.name}
                    onChange={(e) => handleRoleChange('buyer', 'name', e.target.value)}
                    placeholder="Enter buyer name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="buyerPhone">Buyer Phone</label>
                  <input
                    type="tel"
                    id="buyerPhone"
                    value={formData.roles.buyer.phone}
                    onChange={(e) => handleRoleChange('buyer', 'phone', e.target.value)}
                    placeholder="e.g., 0724 931504 or +254 724 931504"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="buyerEmail">Buyer Email</label>
                  <input
                    type="email"
                    id="buyerEmail"
                    value={formData.roles.buyer.email}
                    onChange={(e) => handleRoleChange('buyer', 'email', e.target.value)}
                    placeholder="Enter buyer email"
                  />
                </div>
              </div>

              <div className="role-group">
                <h4>Owner</h4>
                <div className="form-group">
                  <label htmlFor="ownerName">Owner Name</label>
                  <input
                    type="text"
                    id="ownerName"
                    value={formData.roles.owner.name}
                    onChange={(e) => handleRoleChange('owner', 'name', e.target.value)}
                    placeholder="Enter owner name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="ownerPhone">Owner Phone</label>
                  <input
                    type="tel"
                    id="ownerPhone"
                    value={formData.roles.owner.phone}
                    onChange={(e) => handleRoleChange('owner', 'phone', e.target.value)}
                    placeholder="e.g., 0724 931504 or +254 724 931504"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="ownerEmail">Owner Email</label>
                  <input
                    type="email"
                    id="ownerEmail"
                    value={formData.roles.owner.email}
                    onChange={(e) => handleRoleChange('owner', 'email', e.target.value)}
                    placeholder="Enter owner email"
                  />
                </div>
              </div>

              <div className="role-group">
                <h4>User</h4>
                <div className="form-group">
                  <label htmlFor="userName">User Name</label>
                  <input
                    type="text"
                    id="userName"
                    value={formData.roles.user.name}
                    onChange={(e) => handleRoleChange('user', 'name', e.target.value)}
                    placeholder="Enter user name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="userPhone">User Phone</label>
                  <input
                    type="tel"
                    id="userPhone"
                    value={formData.roles.user.phone}
                    onChange={(e) => handleRoleChange('user', 'phone', e.target.value)}
                    placeholder="e.g., 0724 931504 or +254 724 931504"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="userEmail">User Email</label>
                  <input
                    type="email"
                    id="userEmail"
                    value={formData.roles.user.email}
                    onChange={(e) => handleRoleChange('user', 'email', e.target.value)}
                    placeholder="Enter user email"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <h3>Additional Contacts</h3>
              <button 
                type="button" 
                className="btn btn-secondary btn-sm"
                onClick={addOtherContact}
              >
                Add Contact
              </button>
            </div>

            {formData.otherContacts.map((contact, index) => (
              <div key={index} className="other-contact">
                <div className="contact-header">
                  <span>Contact {index + 1}</span>
                  <button 
                    type="button"
                    className="remove-button"
                    onClick={() => removeOtherContact(index)}
                  >
                    Remove
                  </button>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      value={contact.name}
                      onChange={(e) => updateOtherContact(index, 'name', e.target.value)}
                      placeholder="Contact name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={contact.phone}
                      onChange={(e) => updateOtherContact(index, 'phone', e.target.value)}
                      placeholder="Phone number"
                    />
                  </div>

                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={contact.email}
                      onChange={(e) => updateOtherContact(index, 'email', e.target.value)}
                      placeholder="Email address"
                    />
                  </div>
                </div>
              </div>
            ))}

            {formData.otherContacts.length === 0 && (
              <div className="empty-contacts">
                <p>No additional contacts added yet.</p>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (isEditing ? 'Update Customer' : 'Create Customer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomersForm;
